import { useEffect, useRef, useState } from "react";
import ReservationHeader from "../Commun/ReservationHeader";
import ReservationSidebar from "../Commun/ReservationSidebar";
import { useProperty } from "../../PropertyContext";
import { ResDashboRoomInventory } from "../../Services/Service";
import flatpickr from "flatpickr";
import { format, addDays, differenceInDays } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import { resetInventoryState, updateRoomInventory } from "../../Redux/Slices/reseinventorySlice";
import swal from "sweetalert";

export const UpdateRoomInventory = () => {
    const dispatch = useDispatch();

    const { loading: invLoading, success, error } = useSelector((state) => state.inventory);

    // 👉 edited cells store
    const [editedCells, setEditedCells] = useState([]);
    const [roomCodeMap, setRoomCodeMap] = useState({});
    const inputRef = useRef(null);
    const { selectedProperty } = useProperty();

    const [loading, setLoading] = useState(false);
    const [roomData, setRoomData] = useState({});

    // 👉 editable state
    const [editingCell, setEditingCell] = useState(null);
    const [editedValue, setEditedValue] = useState("");

    const [selectedDates, setSelectedDates] = useState(() => {
        const today = new Date();
        const days = [];
        for (let i = 0; i < 7; i++) {
            days.push(addDays(today, i));
        }
        return days;
    });

    // 👉 date picker
    useEffect(() => {
        flatpickr(inputRef.current, {
            mode: "range",
            dateFormat: "d M Y",
            defaultDate: [selectedDates[0], selectedDates[selectedDates.length - 1]],
            onChange: function (selectedDatesArray) {
                if (selectedDatesArray.length === 2) {
                    const start = selectedDatesArray[0];
                    const end = selectedDatesArray[1];
                    const daysDiff = differenceInDays(end, start);
                    const daysArray = [];

                    for (let i = 0; i <= daysDiff; i++) {
                        daysArray.push(addDays(start, i));
                    }

                    setSelectedDates(daysArray);
                }
            },
        });
    }, []);

    // 👉 API call
    useEffect(() => {
        const fetchInventory = async () => {
            if (selectedDates.length < 2) return;

            const start = format(selectedDates[0], "yyyy-MM-dd");
            const end = format(selectedDates[selectedDates.length - 1], "yyyy-MM-dd");

            try {
                setLoading(true);
                setRoomData({});

                const data = await ResDashboRoomInventory(
                    start,
                    end,
                    selectedProperty.PropertyID
                );

                const grouped = {};
                const roomMap = {};

                data.forEach((item) => {
                    const dateStr = format(new Date(item.Date), "yyyy-MM-dd");

                    if (!grouped[item.RoomTypeCode]) {
                        grouped[item.RoomTypeCode] = {};
                    }

                    grouped[item.RoomTypeCode][dateStr] = item.Count;

                    roomMap[item.RoomTypeCode] = item.RoomCode;
                });

                setRoomData(grouped);
                setRoomCodeMap(roomMap);
            } catch (err) {
                console.error("Error fetching inventory:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchInventory();
    }, [selectedDates, selectedProperty]);

    // 👉 click cell
    const handleCellClick = (roomType, dateStr, value) => {
        setEditingCell({ roomType, dateStr, roomCode: roomCodeMap[roomType], });
        setEditedValue(value);
    };

    // 👉 change value
    const handleValueChange = (e) => {
        setEditedValue(e.target.value);
    };

    // 👉 save value
    const handleBlur = () => {
        if (!editingCell) return;

        const { roomType, dateStr, roomCode } = editingCell;

        // UI update
        setRoomData((prev) => ({
            ...prev,
            [roomType]: {
                ...prev[roomType],
                [dateStr]: Number(editedValue),
            },
        }));

        // 👉 track edited cell
        setEditedCells((prev) => {
            const exists = prev.find(
                (c) =>
                    c.roomType === roomType && c.dateStr === dateStr
            );

            if (exists) {
                return prev.map((c) =>
                    c.roomType === roomType && c.dateStr === dateStr
                        ? { ...c, Count: Number(editedValue) }
                        : c
                );
            } else {
                return [
                    ...prev,
                    {
                        roomType,
                        dateStr,
                        Count: Number(editedValue),
                        RoomCode: roomCode,
                    },
                ];
            }
        });

        setEditingCell(null);
    };

    // 👉 enter press
    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            handleBlur();
        }
    };

    const handleSubmit = async () => {
        if (editedCells.length === 0) {
            alert("No changes to update");
            return;
        }

        const PID = selectedProperty.PropertyID;

        try {
            for (let cell of editedCells) {
                await dispatch(
                    updateRoomInventory({
                        PID: PID,
                        Date: cell.dateStr,
                        Count: cell.Count,
                        RoomTypeCode: cell.RoomCode,
                    })
                );
            }
        } catch (err) {
            console.log(err);
        }
    };

    useEffect(() => {
        if (success) {
            swal("Room Inventory Update Successfully", {
                icon: "success",
                buttons: {
                    confirm: {
                        text: "OK",
                        className: "btn btn-success",
                    },
                },
            });

            setEditedCells([]);
            dispatch(resetInventoryState());
        }

        if (error) {
            alert(error);
            dispatch(resetInventoryState());
        }
    }, [success, error]);

    return (
        <>
            {loading && (
                <div className="fullscreen-loader">
                    <div className="fas fa-spinner fa-spin fa-3x"></div>
                </div>
            )}

            {invLoading && (
                <div className="fullscreen-loader">
                    <div className="fas fa-spinner fa-spin fa-3x"></div>
                </div>
            )}

            <div className="wrapper">
                <ReservationSidebar />

                <div className="main-panel">
                    <ReservationHeader />

                    <div className="container">
                        <div className="page-inner">
                            <div className="row">
                                <div className="col-md-12">
                                    <h3 className="page-title">Update Room Inventory</h3>

                                    <div className="card">
                                        {/* HEADER */}
                                        <div className="card-header d-flex justify-content-between align-items-center">
                                            <h5 className="card-title">
                                                Total Available Room's
                                            </h5>

                                            <div className="d-flex">
                                                <i className="fas fa-calendar fs-3 me-2 mt-2"></i>

                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    ref={inputRef}
                                                    style={{ width: "17rem" }}
                                                />
                                            </div>
                                        </div>

                                        {/* TABLE */}
                                        <div className="card-body availability-table-wrapper">
                                            <table className="table table-bordered">
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: "300px" }}>
                                                            Room Type
                                                        </th>

                                                        {selectedDates.map((d, i) => (
                                                            <th key={i}>
                                                                {format(d, "MMM d")}
                                                            </th>
                                                        ))}
                                                    </tr>
                                                </thead>

                                                <tbody>
                                                    {Object.keys(roomData).map(
                                                        (roomType, idx) => (
                                                            <tr key={idx}>
                                                                <td className="fw-bold">
                                                                    {roomType}
                                                                </td>

                                                                {selectedDates.map((d, i) => {
                                                                    const dateStr = format(
                                                                        d,
                                                                        "yyyy-MM-dd"
                                                                    );

                                                                    const count =
                                                                        roomData[roomType][
                                                                        dateStr
                                                                        ] || 0;

                                                                    return (
                                                                        <td
                                                                            key={i}
                                                                            onClick={() =>
                                                                                handleCellClick(
                                                                                    roomType,
                                                                                    dateStr,
                                                                                    count,
                                                                                )
                                                                            }
                                                                            style={{
                                                                                cursor: "pointer",
                                                                            }}
                                                                        >
                                                                            {editingCell &&
                                                                                editingCell.roomType === roomType &&
                                                                                editingCell.dateStr === dateStr ? (
                                                                                <input
                                                                                    type="number"
                                                                                    value={editedValue}
                                                                                    onChange={handleValueChange}
                                                                                    onBlur={handleBlur}
                                                                                    onKeyDown={handleKeyDown}
                                                                                    autoFocus
                                                                                    className="form-control form-control-sm"
                                                                                    style={{
                                                                                        width: "70px",
                                                                                    }}
                                                                                />
                                                                            ) : count === 0 ? (
                                                                                <span className="badge bg-danger">
                                                                                    0
                                                                                </span>
                                                                            ) : (
                                                                                count
                                                                            )}
                                                                        </td>
                                                                    );
                                                                })}
                                                            </tr>
                                                        )
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* FOOTER */}
                                        <div className="card-footer text-end">
                                            <button className="btn btn-primary btn-sm" onClick={handleSubmit}>
                                                Submit
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};